import React, { useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { AppProvider } from "./context/AppProvider";
import { AppRoutes } from "./routes/Router";
import ReactGA from "react-ga";
import ErrorBoundary from "./components/ErrorBoundary";
import AnalyticsTracker from "./components/AnalyticsTracker";
import InstallPrompt from "./components/InstallPrompt";
import GA4ErrorDashboard from "./components/GA4ErrorDashboard";
import CacheManager from "./components/CacheManager";
import useErrorHandler from "./hooks/useErrorHandler";
import "./App.css";

// Inicializar Google Analytics (mantener para compatibilidad)
ReactGA.initialize("G-75FQ87LFHC");

// Importar Firebase para inicializar Analytics
import "./firebase";

const App = () => {
  // Usar nuestro hook personalizado para manejo de errores
  useErrorHandler();

  useEffect(() => {
    // Suprimir errores de extensiones del navegador
    const originalError = console.error;
    const originalWarn = console.warn;
    
    console.error = (...args) => {
      const errorMessage = args.join(' ');
      
      // Suprimir errores específicos de extensiones
      if (
        errorMessage.includes('inpage.js') ||
        errorMessage.includes('Cannot read properties of null') ||
        errorMessage.includes('chrome-extension://') ||
        errorMessage.includes('moz-extension://') ||
        errorMessage.includes('extension://')
      ) {
        return; // No mostrar estos errores
      }
      
      originalError.apply(console, args);
    };
    
    console.warn = (...args) => {
      const warnMessage = args.join(' ');
      
      // Suprimir warnings específicos de extensiones
      if (
        warnMessage.includes('inpage.js') ||
        warnMessage.includes('chrome-extension://') ||
        warnMessage.includes('moz-extension://') ||
        warnMessage.includes('extension://')
      ) {
        return; // No mostrar estos warnings
      }
      
      originalWarn.apply(console, args);
    };

    // Manejador global de errores no capturados
    const handleUnhandledError = (event) => {
      const error = event.error || event.reason;
      const errorMessage = error?.message || error?.toString() || '';
      
      // Suprimir errores de extensiones
      if (
        errorMessage.includes('inpage.js') ||
        errorMessage.includes('Cannot read properties of null') ||
        errorMessage.includes('chrome-extension://') ||
        errorMessage.includes('moz-extension://') ||
        errorMessage.includes('extension://') ||
        errorMessage.includes('TypeError: Cannot read properties of null')
      ) {
        event.preventDefault();
        return false;
      }
    };

    // Manejador para promesas rechazadas
    const handleUnhandledRejection = (event) => {
      const error = event.reason;
      const errorMessage = error?.message || error?.toString() || '';
      
      // Suprimir errores de extensiones
      if (
        errorMessage.includes('inpage.js') ||
        errorMessage.includes('Cannot read properties of null') ||
        errorMessage.includes('chrome-extension://') ||
        errorMessage.includes('moz-extension://') ||
        errorMessage.includes('extension://') ||
        errorMessage.includes('TypeError: Cannot read properties of null')
      ) {
        event.preventDefault();
        return false;
      }
    };

    // Agregar los manejadores
    window.addEventListener('error', handleUnhandledError);
    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    // Cleanup function
    return () => {
      console.error = originalError;
      console.warn = originalWarn;
      window.removeEventListener('error', handleUnhandledError);
      window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    };
  }, []);

  return (
    <ErrorBoundary>
      <AppProvider>
        <BrowserRouter>
          <AnalyticsTracker />
          <InstallPrompt />
          <AppRoutes />
          <GA4ErrorDashboard />
          <CacheManager />
        </BrowserRouter>
      </AppProvider>
    </ErrorBoundary>
  );
};

export default App;
